// v2.9 — Frontend Config Hydration. The app fetches real backend state from
// GET /api/app/config on load, re-checks the tiny /version endpoint on window
// focus and every 60s, and re-hydrates (flags, catalog, shop, stylist) the
// moment the Founder Console changes anything. No hardcoded assumptions.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { resetFeatureFlagCache } from '../hooks/useFeatureFlag';

const API = window.__SG_API_BASE__;

const AppConfigContext = createContext({ config: null, ready: false, refresh: () => {} });

export const AppConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [ready, setReady] = useState(false);
  const versionRef = useRef(0);

  const fetchConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/app/config`, { withCredentials: true });
      versionRef.current = r.data?.config_version || 0;
      setConfig(r.data);
    } catch {
      // Keep the last known config — never blank the app on a transient failure.
    } finally {
      setReady(true);
    }
  }, []);

  const checkVersion = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/app/config/version`, { withCredentials: true });
      const v = r.data?.config_version || 0;
      if (v && v !== versionRef.current) {
        resetFeatureFlagCache();
        await fetchConfig();
      }
    } catch { /* silent — retried on next tick/focus */ }
  }, [fetchConfig]);

  useEffect(() => {
    fetchConfig();
    const onFocus = () => checkVersion();
    const onVis = () => { if (document.visibilityState === 'visible') checkVersion(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    const t = setInterval(checkVersion, 60000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(t);
    };
  }, [fetchConfig, checkVersion]);

  return (
    <AppConfigContext.Provider value={{ config, ready, refresh: fetchConfig }}>
      {children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = () => useContext(AppConfigContext);
